import { Component, effect } from "@angular/core";
import { CommonModule } from "@angular/common";
import { environment } from "src/environments/environment";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { TypeaheadMatch, TypeaheadModule } from "ngx-bootstrap/typeahead";
import { HotToastService } from "@ngneat/hot-toast";
import { AccountantService } from "src/app/dashboard/main/accountant/accountant.service";
import { BsModalRef } from "ngx-bootstrap/modal";
import { DashboardStore } from "src/app/dashboard/dashboard.store";
import { Subject } from "rxjs";
import { ActiveClientsOnlyPipe } from "src/app/dashboard/main/common/active-clients-only.pipe";
import { DashboardService } from "src/app/dashboard/dashboard.service";
import {
  Account,
  WatchedAccount,
  XeroConnectedWorkspace,
  XeroIntegrationService,
} from "src/app/dashboard/xero-integration.service";
import { fromStorage } from "src/app/from-storage.function";

@Component({
  selector: "xero-integration-wizard",
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TypeaheadModule,
    ActiveClientsOnlyPipe,
  ],
  templateUrl: "./xero-integration-wizard.component.html",
  styleUrls: [
    "./xero-integration-wizard.component.scss",
    "../../../../../../dashboard/main/accountant/qbo-accounts-modal/qbo-accounts-modal.component.scss",
  ],
  standalone: true,
})
export class XeroIntegrationWizardComponent {
  step = 1;
  loading: boolean = false;
  onClose: any;
  workspaces: any = [];
  companiesAccounts: any = {};
  selectedCompany: any = null;
  XeroAccountIsIntegrated: boolean = false;
  companyAccounts = [];
  account: any = {};
  selectedAccount = {} as Account;
  shouldImport: boolean = true;
  connectedWorkspaces: XeroConnectedWorkspace[] = [];
  watchedAccounts: WatchedAccount[] = [];
  connectionUuid: string = "";
  availableAccounts: Account[] = [];

  newCompanyConnectionStatus = fromStorage<boolean | null>(
    "newCompanyConnectionStatus",
  );

  constructor(
    private toastr: HotToastService,
    private accountantService: AccountantService,
    public bsModalRef: BsModalRef,
    private dashboardStore: DashboardStore,
    private dashboardService: DashboardService,
    private xeroService: XeroIntegrationService,
  ) {
    effect(() => {
      console.log(
        "Syncing storage on wizard",
        this.newCompanyConnectionStatus(),
      );

      if (this.newCompanyConnectionStatus() == true) {
        this._getConnectedWorkspaces();

        this.toastr.success(`Authenticated in Xero.`);
        this.XeroAccountIsIntegrated = true;

        this.xeroService
          .getIntegrationStatus(this.selectedCompany.id)
          .subscribe(
            (res: any) => {
              if (res.integrated == true) {
                this.loading = false;
                this.XeroAccountIsIntegrated = true;
                this.goToXeroAccountsSelect();
              }
            },
            (err: any) => {
              this.toastr.error(err.message);
              this.loading = false;
            },
          );
        if (this.step === 3) {
          this.loading = false;
        }
      }
    });
  }

  ngOnInit(): void {
    this.getAvailableWorkspaces();
    this.newCompanyConnectionStatus.set(null);
  }

  selectWorkspaceForIntegration(event: TypeaheadMatch) {
    this.selectedCompany = event.item;
  }

  getAvailableWorkspaces() {
    this.workspaces = this.dashboardStore.clients;
    this.dashboardStore.clientsLoaded.subscribe((clients) => {
      this.workspaces = this.dashboardStore.clients;
      console.log("WORKSPACES", this.workspaces);
    });
  }

  _getConnectedWorkspaces() {
    this.loading = true;
    this.xeroService
      .getConnectedWorkspaces()
      .subscribe((res: XeroConnectedWorkspace[]) => {
        this.connectedWorkspaces = res;
      })
      .add(() => (this.loading = false));
  }

  closeModal() {
    // Emit event to notify parent to reload data
    if (this.onClose && this.onClose.next) {
      this.onClose.next(true);
    }
    this.bsModalRef.hide();
  }

  launchAuthenticationForm(workspaceId: number) {
    this.loading = true;
    let isListenerExhausted = false;

    const path = `${environment.apiUrl}/integration/xero/auth?integrate=${workspaceId}`;

    let parameters = "location=1,width=800,height=650";
    parameters +=
      ",left=" + (screen.width - 800) / 2 + ",top=" + (screen.height - 650) / 2;

    const win = window.open(path, "connectPopup", parameters);
  }

  goToXeroAccountsSelect() {
    this.step = 3;
    this.loading = true;
    this.xeroService.getConnectedWorkspaces().subscribe(
      (res: XeroConnectedWorkspace[]) => {
        this.connectedWorkspaces = res;
        console.log("CONNECTED WORKSPACES", this.connectedWorkspaces);
        // Filter workspace by selected company
        let company = this.connectedWorkspaces.find(
          (workspace: XeroConnectedWorkspace) =>
            workspace.company === this.selectedCompany.id,
        );
        this.connectionUuid = company.uuid;
        this.loading = false;
        this.getCompanyAccounts();
      },
      (err: any) => {
        this.loading = false;
        this.toastr.error(err.message);
      },
    );

    this.xeroService
      .getAvailableAccountsByCompany(this.selectedCompany.id)
      .subscribe(
        (res: any) => {
          console.log("INTEGRATION STATUS", res);
          this.availableAccounts = res.accounts;
        },
        (err: any) => {
          this.toastr.error(err.message);
        },
      );
  }

  getCompanyAccounts() {
    this.loading = true;
    this.xeroService
      .getCompanyAccounts(this.connectionUuid)
      .subscribe((res: WatchedAccount[]) => {
        this.watchedAccounts = res.sort(
          (a, b) => +a.external_object.Code - +b.external_object.Code,
        );
        this.loading = false;
      });
  }

  selectAccount(event: TypeaheadMatch) {
    this.selectedAccount = event.item;
  }

  addAccountToCompany() {
    this.xeroService
      .addAccountToCompany(this.connectionUuid, this.selectedAccount)
      .subscribe(
        (res: any) => {
          this.getCompanyAccounts();
          this.selectedAccount = {} as Account;
        },
        (err: any) => {},
      );
  }

  removeAccountFromCompany(removedAccountID: string) {
    this.xeroService.removeAccountFromCompany(removedAccountID).subscribe(
      (res: any) => {
        this.watchedAccounts = this.watchedAccounts.filter(
          (wAccount: WatchedAccount) => wAccount.uuid !== removedAccountID,
        );
      },
      (err: any) => {},
    );
  }

  finishWizard() {
    this.step = 5;
    if (this.shouldImport) {
      // Code to sync 90 days, does it exists for Xero too?
    }
  }
}
